import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Database, 
  FileCode, 
  ChevronDown,
  Search,
  Filter
} from 'lucide-react';
import { CourseSyncService, CourseSyncReport, SyncResult } from '@/services/courseSyncService';
import { toast } from 'sonner';

const CourseSyncDashboard: React.FC = () => {
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'synced' | 'mismatched'>('all');
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadSyncData();
  }, []);

  const loadSyncData = async () => {
    setLoading(true);
    try {
      const result = await CourseSyncService.auditAllCourses();
      setSyncResult(result);
    } catch (error) {
      console.error('Failed to load sync data:', error);
      toast.error('Failed to load sync data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCourse = (courseId: string) => {
    setExpandedCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  const filteredReports = syncResult?.reports.filter(report => {
    const matchesSearch = report.courseTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.courseId.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'synced' && report.isSynced) ||
                         (filterStatus === 'mismatched' && !report.isSynced);
    
    return matchesSearch && matchesFilter;
  }) || [];

  const getSyncStatusBadge = (status: CourseSyncReport['syncStatus']) => {
    switch (status) {
      case 'synced':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Synced</Badge>;
      case 'static_missing':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Static Missing</Badge>;
      case 'database_missing':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">DB Missing</Badge>;
      case 'both_missing':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Both Missing</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Course Sync Dashboard</h1>
        <p className="text-muted-foreground">
          Compare database modules vs static code definitions
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Courses</p>
                <p className="text-3xl font-bold text-foreground">
                  {syncResult?.totalCourses || 0}
                </p>
              </div>
              <Database className="h-10 w-10 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Synced</p>
                <p className="text-3xl font-bold text-green-400">
                  {syncResult?.syncedCourses || 0}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Mismatched</p>
                <p className="text-3xl font-bold text-amber-400">
                  {syncResult?.mismatchedCourses || 0}
                </p>
              </div>
              <AlertTriangle className="h-10 w-10 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Last Audit</p>
                <p className="text-sm font-medium text-foreground">
                  {syncResult?.generatedAt 
                    ? new Date(syncResult.generatedAt).toLocaleTimeString()
                    : 'Never'}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={loadSyncData}
                disabled={loading}
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            All
          </Button>
          <Button
            variant={filterStatus === 'synced' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('synced')}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Synced
          </Button>
          <Button
            variant={filterStatus === 'mismatched' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('mismatched')}
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Mismatched
          </Button>
        </div>
      </div>

      {/* Course List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle>Course Sync Status</CardTitle>
          <CardDescription>
            {filteredReports.length} courses shown
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No courses match your filters
                </div>
              ) : (
                filteredReports.map(report => (
                  <Collapsible
                    key={report.courseId}
                    open={expandedCourses.has(report.courseId)}
                    onOpenChange={() => toggleCourse(report.courseId)}
                  >
                    <CollapsibleTrigger asChild>
                      <div className={`flex items-center justify-between p-4 rounded-lg cursor-pointer hover:bg-accent/50 transition-colors ${
                        report.isSynced ? 'bg-green-500/5' : 'bg-amber-500/5'
                      }`}>
                        <div className="flex items-center gap-4">
                          <ChevronDown className={`h-4 w-4 transition-transform ${
                            expandedCourses.has(report.courseId) ? 'rotate-180' : ''
                          }`} />
                          <div>
                            <p className="font-medium text-foreground">{report.courseTitle}</p>
                            <p className="text-sm text-muted-foreground">{report.courseId}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 text-sm">
                            <FileCode className="h-4 w-4 text-blue-400" />
                            <span className="text-muted-foreground">Static:</span>
                            <span className="font-medium">{report.staticModuleCount}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Database className="h-4 w-4 text-green-400" />
                            <span className="text-muted-foreground">DB:</span>
                            <span className="font-medium">{report.databaseModuleCount}</span>
                          </div>
                          {getSyncStatusBadge(report.syncStatus)}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      {report.mismatches.length > 0 && (
                        <div className="ml-8 mt-2 p-4 bg-muted/30 rounded-lg">
                          <p className="text-sm font-medium text-muted-foreground mb-3">
                            Module Mismatches ({report.mismatches.length})
                          </p>
                          <div className="space-y-2">
                            {report.mismatches.map((mismatch, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-2 bg-background rounded border border-border"
                              >
                                <div>
                                  <p className="text-sm font-medium">{mismatch.moduleTitle}</p>
                                  <p className="text-xs text-muted-foreground">{mismatch.moduleId}</p>
                                </div>
                                <div className="flex gap-2">
                                  {mismatch.existsInDatabase && (
                                    <Badge variant="outline" className="text-green-400 border-green-400/30">
                                      <Database className="h-3 w-3 mr-1" />
                                      In DB
                                    </Badge>
                                  )}
                                  {mismatch.existsInStatic && (
                                    <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                                      <FileCode className="h-3 w-3 mr-1" />
                                      In Static
                                    </Badge>
                                  )}
                                  {!mismatch.existsInStatic && (
                                    <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                                      Missing Static
                                    </Badge>
                                  )}
                                  {!mismatch.existsInDatabase && (
                                    <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                                      Missing DB
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {report.mismatches.length === 0 && (
                        <div className="ml-8 mt-2 p-4 bg-green-500/5 rounded-lg">
                          <p className="text-sm text-green-400 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            All modules are synchronized between database and static code
                          </p>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseSyncDashboard;
